// src/view/linkAccount/components/PhoneVerification.tsx

import React, { useEffect, useState } from "react";
import { Div, Font, Input } from "../../../styles/commonStyle";
import { cardTitleColor2, fontGreyColor } from "../../../styles/theme";
import { Flex, RoundButton, Line } from "../style";
import { CheckBox } from "../../../styles/commonStyle";
import { FieldErrors, FieldValues, UseFormGetValues, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { ResponseModel, RESULT } from "../../../model/response";
import usePost from "../../../hooks/usePost";
import { certifyPhoneSelector, checkAuthSelector } from "../../../recoil/variableAtom";
import { certifyPhoneState, checkAuthState, loadingState } from "../../../recoil/atoms";
import Timer from "../../../components/timer/timer";
import { useRecoilState } from "recoil";
import PrivacyAgreementModal from "../../../components/modal/privacyAgreementModal";
import { onInputOnlyNumber } from "../../../validation";

interface PhoneVerificationProps {
    isPhoneVerification?: boolean;
    setIsPhoneVerification?: (checked: boolean) => void;
    title: React.ReactNode;
    subText: React.ReactNode;
    margin?: string;
    hasLine?: boolean;
    maxWidth?: string;
    isEdit?: boolean;
    register?: UseFormRegister<FieldValues>;
    errors?: FieldErrors<FieldValues>;
    setValue?: UseFormSetValue<FieldValues>;
    getValues?: UseFormGetValues<FieldValues>;
    phoneNumber?: {
        first: string;
        second: string;
        third: string;
    };
    handleCheckCert?: () => void;
    type?: 'AUTH' | 'COMMON';
}

export default function PhoneVerification({
    isPhoneVerification,
    setIsPhoneVerification,
    title,
    subText,
    margin,
    hasLine,
    maxWidth,
    isEdit = false,
    register,
    errors,
    setValue,
    getValues,
    phoneNumber,
    handleCheckCert,
    type = 'AUTH'
}: PhoneVerificationProps) {
    const [isLoading, setLoading] = useRecoilState(loadingState);
    const [getBody, setBody] = useState<any>();
    const [getAuthBody, setAuthBody] = useState<any>();
    const [sendAuthCode, setsendAuthCode] = useState(false);
    const [timer, setTimer] = useState(false);
    const [timerKey, setTimerKey] = useState(0);
    const [authCode, setAuthCode] = useState('');

    const certResponse: ResponseModel<any> = usePost({
        recoilSelector: certifyPhoneSelector,
        data: getBody,
        recoilState: certifyPhoneState,
    });
    const checkResponse: ResponseModel<any> = usePost({
        recoilSelector: checkAuthSelector,
        data: getAuthBody,
        recoilState: checkAuthState,
    });
    const handleSendAuthCode = () => {
        setLoading(true);
        var phone;
        if (getValues) {
            phone = `${getValues('phoneFirst')}${getValues('phoneSecond')}${getValues('phoneThird')}`;
        } else {
            phone = `${phoneNumber?.first}${phoneNumber?.second}${phoneNumber?.third}`;
        }
        setBody({
            "ty": type === "AUTH" ? "AUTH" : "COMMON",
            "phone": phone
        });
    }
    const handleCheckAuthCode = () => {
        setLoading(true);
        if (getValues) {
            setAuthBody({
                "authCode": getValues('authCode')
            });
        } else {
            setAuthBody({
                "authCode": authCode
            });
        }
    }
    useEffect(() => {
        if (certResponse != undefined) {
            if (certResponse.result === RESULT.OK) {
                setsendAuthCode(true);
                setTimerKey(prev => prev + 1);
                alert("인증번호가 발송되었습니다.");
            } else {
                alert(certResponse.msg);
            }
        }
    }, [certResponse]);

    useEffect(() => {
        if (checkResponse != undefined) {
            if (checkResponse.result === RESULT.OK) {
                alert("인증번호가 확인되었습니다.");
                setsendAuthCode(false);
                setTimer(false);
                if (handleCheckCert) { handleCheckCert() }
            } else {
                alert(checkResponse.msg);
            }
        }
    }, [checkResponse]);

    useEffect(() => {
        if (timer) {
            alert("인증 제한 시간이 만료되었습니다. 인증번호를 다시 발송해주세요.");
            setTimer(false);
            setsendAuthCode(false);
            setTimerKey(0); // 타이머가 끝나면 timerKey를 0으로 리셋
        }
    }, [timer]);

    const phoneFields = {
        first: register ? register('phoneFirst', {
            required: !isEdit && '전화번호를 입력해주세요',
            pattern: {
                value: /^[0-9]{3}$/,
                message: '3자리 숫자를 입력해주세요'
            }
        }) : undefined,
        second: register ? register('phoneSecond', {
            required: !isEdit && '전화번호를 입력해주세요',
            pattern: {
                value: /^[0-9]{4}$/,
                message: '4자리 숫자를 입력해주세요'
            }
        }) : undefined,
        third: register ? register('phoneThird', {
            required: !isEdit && '전화번호를 입력해주세요',
            pattern: {
                value: /^[0-9]{4}$/,
                message: '4자리 숫자를 입력해주세요'
            }
        }) : undefined
    };
    return (
        <>
            {hasLine && <Line />}
            <Flex style={{ maxWidth: maxWidth || "700px", margin }} flexDirection="column" justifyContent="center" alignItems="start" gap="20px">
                <Div>
                    <Font fontSize="1.5rem" fontWeight="500" color={cardTitleColor2} changeFontSize="18px">
                        {title}
                    </Font>
                    <Font style={{ marginTop: "10px" }} fontSize="1rem" color={fontGreyColor} changeFontSize="0.75rem">
                        {subText}
                    </Font>
                </Div>
                <Flex changeFlexDirection="column" changeAlignItems="start" gap="10px">
                    <Font style={{ flexShrink: 0, marginRight: "10px" }} fontSize="1.125rem" fontWeight="400" color={cardTitleColor2} changeFontSize="1rem">
                        휴대폰 번호
                    </Font>
                    <Flex style={{ marginRight: "10px" }} gap="6px">
                        <Input
                            style={{ flexShrink: 1, height: "40px" }}
                            type="tel"
                            value={!isEdit ? phoneNumber?.first : undefined}
                            disabled={!isEdit}
                            maxLength={3}
                            {...phoneFields.first}
                            borderRadius="0.375rem"
                        />
                        -
                        <Input
                            style={{ flexShrink: 1, height: "40px" }}
                            type="tel"
                            value={!isEdit ? phoneNumber?.second : undefined}
                            disabled={!isEdit}
                            maxLength={4}
                            {...phoneFields.second}
                            borderRadius="0.375rem"
                        />
                        -
                        <Input
                            style={{ flexShrink: 1, height: "40px" }}
                            type="tel"
                            value={!isEdit ? phoneNumber?.third : undefined}
                            disabled={!isEdit}
                            maxLength={4}
                            {...phoneFields.third}
                            borderRadius="0.375rem"
                        />
                    </Flex>
                    <RoundButton isDisabled={isPhoneVerification != null ? (!isPhoneVerification || timerKey > 0) : timerKey > 0} onClick={() => {
                        isPhoneVerification != null ?
                            (isPhoneVerification && !timerKey && handleSendAuthCode())
                            : (!timerKey && handleSendAuthCode());
                    }}>인증번호 발송</RoundButton>
                </Flex>
                {
                    isPhoneVerification != null ? <Flex>
                        <label style={{ display: "flex", gap: "10px", fontSize: "0.875rem", color: fontGreyColor }}>
                            <CheckBox
                                type="checkbox"
                                name="phoneAuthPrivacyConsent"
                                checked={isPhoneVerification}
                                onChange={(e) => {
                                    setIsPhoneVerification && setIsPhoneVerification(e.target.checked);
                                }}
                            />
                            <Font fontSize="18px" changeFontSize="0.875rem">휴대폰 인증을 위한 개인정보제공동의</Font>
                        </label>
                    </Flex> : null
                }
               
                <Flex alignItems="start" changeFlexDirection="column" changeAlignItems="start" gap="10px">
                    <Font style={{ flexShrink: 0, marginRight: "10px", height: "40px" }} fontSize="1.125rem" fontWeight="400" color={cardTitleColor2} changeFontSize="1rem">
                        인증번호
                    </Font>
                    <Flex>
                        <Flex flexDirection="column" alignItems="start" style={{ marginRight: "10px" }}>
                            <Input
                                style={{ height: "40px" }}
                                type="tel"
                                placeholder="인증번호 6자리를 입력해주세요."
                                maxLength={6}
                                borderRadius="0.375rem"
                                {...(register ? register('authCode') : {
                                    onChange: (e) => {
                                        setAuthCode(e.target.value);
                                    }
                                })}
                            />
                            {sendAuthCode && (
                                <Font style={{ marginTop: "8px" }} fontSize="0.875rem" color={fontGreyColor}>남은 시간: <Timer key={timerKey} mm={3} ss={0} setTimer={setTimer}></Timer></Font>
                            )}
                        </Flex>
                        <RoundButton onClick={() => { sendAuthCode && handleCheckAuthCode() }} isDisabled={!sendAuthCode}>인증번호 확인</RoundButton>
                    </Flex>
                </Flex>
            </Flex>
        </>
    );
}